/**
 * Dashboard Page Component
 *
 * Main dashboard router - redirects to role-specific dashboard
 *
 * @module pages/Dashboard
 */

import React from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminDashboard from './dashboard/AdminDashboard';
import SupervisorDashboard from './dashboard/SupervisorDashboard';
import ClientDashboard from './dashboard/ClientDashboard';
import '../styles/dashboard.css';

const Dashboard = () => {
  const { profile, loading } = useAuth();
  const navigate = useNavigate();

  // Still fetching — show spinner
  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  // Auth finished but profile is null → connection/server problem, NOT a permissions issue
  if (!profile) {
    return (
      <div className="text-center py-5">
        <i className="bi bi-wifi-off fs-1 text-muted"></i>
        <h5 className="mt-3 text-muted">Sin conexión al servidor</h5>
        <p className="text-muted small">
          No se pudo cargar tu perfil. Verifica tu conexión e intenta recargar.
        </p>
        <Button variant="outline-primary" size="sm" onClick={() => window.location.reload()}>
          <i className="bi bi-arrow-clockwise me-1"></i>Recargar
        </Button>
      </div>
    );
  }

  // Route to appropriate dashboard based on user role.
  //
  // Note: there is deliberately no 'technician' case here. PrivateLayout
  // redirects technicians away from /dashboard to their dedicated portal at
  // /tecnico/dashboard (see technicianAllowedPaths), so a technician never
  // reaches this component. The desktop TechnicianDashboard that used to live
  // in ./dashboard/TechnicianDashboard was unreachable dead code and was
  // removed in #310. If a desktop technician view is ever wanted, add
  // '/dashboard' to technicianAllowedPaths and build it deliberately.
  const renderDashboard = () => {
    switch (profile.role) {
      case 'admin':
        return <AdminDashboard />;
      case 'supervisor':
        return <SupervisorDashboard />;
      case 'client':
        return <ClientDashboard />;
      default:
        // Callejón sin salida: decía qué NO se puede hacer y no ofrecía nada
        // que sí. Mismo patrón que la rama "Sin conexión al servidor" de
        // arriba: qué pasó, qué hacer, y un botón.
        return (
          <div className="text-center py-5" data-testid="dashboard-unauthorized">
            <i className="bi bi-shield-lock fs-1 text-warning"></i>
            <h5 className="mt-3">Tu rol no tiene un panel asignado</h5>
            <p className="text-muted small mb-3">
              Podés seguir usando el resto de la aplicación desde el menú lateral, o
              consultar el manual. Si creés que deberías ver un panel acá, pedile al
              administrador que revise tu rol.
            </p>
            <div className="d-flex justify-content-center flex-wrap gap-2">
              <Button variant="primary" size="sm" onClick={() => navigate('/ayuda')}>
                <i className="bi bi-question-circle me-1"></i>Ir a la Ayuda
              </Button>
              <Button variant="outline-primary" size="sm" onClick={() => navigate('/perfil')}>
                <i className="bi bi-person me-1"></i>Ver mi perfil
              </Button>
            </div>
          </div>
        );
    }
  };

  return renderDashboard();
};

export default Dashboard;
